/** Final wizard step — email the customer the combined schedule (new / reply-in-thread), or finish without sending. */
import { useEffect, useState } from "react";
import { Loader2, Mail, Reply, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getAgeingM365Emails, getCaseContactEmails, gcScheduleSendEmail } from "@/lib/api";
import GcCombinedTable from "./GcCombinedTable";
import { Field } from "./GcScheduleSteps";

export default function GcScheduleEmailStep({ caseRow, schedule, combined, defaultIntro, onFinished }) {
  const [emailMode, setEmailMode] = useState("new");
  const [contacts, setContacts] = useState([]);
  const [inbox, setInbox] = useState(null);
  const [replyMsg, setReplyMsg] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(schedule.kind === "one_off" ? `One-off payment confirmation — ${caseRow.customer_name || caseRow.loan_application_id}` : `Payment arrangement — ${caseRow.customer_name || caseRow.loan_application_id}`);
  const [intro, setIntro] = useState(defaultIntro || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getCaseContactEmails(caseRow.case_id).then((r) => {
      setContacts(r.contacts || []);
      const primary = (r.contacts || []).find((c) => c.primary) || (r.contacts || [])[0];
      if (primary) setTo(primary.email);
    }).catch(() => {});
  }, [caseRow.case_id]);

  const loadInbox = async () => {
    if (inbox !== null) return;
    setInbox([]);
    try { const r = await getAgeingM365Emails(caseRow.case_id, { limit: 15 }); setInbox((r.emails || []).filter((e) => !e.is_read_receipt)); }
    catch (e) { toast.error("Couldn't load the case inbox: " + (e?.response?.data?.detail || e.message)); }
  };

  const send = async () => {
    setBusy(true);
    try {
      if (emailMode === "reply") {
        if (!replyMsg) { toast.error("Pick the email you're replying to"); return; }
        const from = replyMsg.from?.emailAddress?.address;
        await gcScheduleSendEmail(caseRow.case_id, schedule.schedule_id, { to: from ? [from] : [], intro: intro || undefined, reply_to_message_id: replyMsg.id, actor: "Ops" });
        toast.success("Schedule sent as a reply in the thread");
      } else {
        const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
        if (!recipients.length) { toast.error("Add at least one recipient"); return; }
        await gcScheduleSendEmail(caseRow.case_id, schedule.schedule_id, { to: recipients, subject, intro: intro || undefined, actor: "Ops" });
        toast.success(`Schedule emailed to ${recipients.join(", ")}`);
      }
      onFinished(true);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const Mode = ({ k, icon, label, tid, onClick }) => (
    <button type="button" onClick={onClick} data-testid={tid}
            className={`flex items-center gap-1.5 border rounded-md px-3 py-1.5 text-xs font-semibold transition ${emailMode === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}>{icon}{label}</button>
  );

  return (
    <div className="space-y-3" data-testid="gc-wiz-email-step">
      <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5" data-testid="gc-wiz-created-banner">
        ✓ {schedule.kind === "one_off" ? "One-off payment" : "Payment schedule"} created in GoCardless ({schedule.instalment_schedule_id || schedule.payment_id}). Send the customer the table below, or finish without emailing.
      </div>
      <div className="flex gap-1.5">
        <Mode k="new" tid="gc-wiz-email-mode-new" icon={<Mail className="w-3.5 h-3.5" />} label="New email" onClick={() => setEmailMode("new")} />
        <Mode k="reply" tid="gc-wiz-email-mode-reply" icon={<Reply className="w-3.5 h-3.5" />} label="Reply to a thread" onClick={() => { setEmailMode("reply"); loadInbox(); }} />
      </div>
      {emailMode === "new" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="To (comma-separate multiple)">
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" className="h-9" data-testid="gc-wiz-email-to" />
            {contacts.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{contacts.map((c) => <button key={c.email} type="button" onClick={() => setTo(c.email)} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 border" data-testid={`gc-wiz-email-contact-${c.email}`}>{c.label} · {c.email}</button>)}</div>}
          </Field>
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" data-testid="gc-wiz-email-subject" /></Field>
        </div>
      ) : (
        <div className="border rounded-md divide-y max-h-48 overflow-auto" data-testid="gc-wiz-email-inbox">
          {inbox === null || inbox.length === 0 ? <div className="text-xs text-muted-foreground p-3">{inbox === null ? "Loading case inbox…" : "No emails found for this customer — use New email instead."}</div>
            : inbox.map((e) => (
              <button key={e.id} type="button" onClick={() => setReplyMsg(replyMsg?.id === e.id ? null : e)} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${replyMsg?.id === e.id ? "bg-amber-50 border-l-2 border-amber-500" : ""}`} data-testid={`gc-wiz-email-thread-${e.id}`}>
                <div className="flex justify-between gap-2"><span className="font-medium truncate">{e.from?.emailAddress?.name || e.from?.emailAddress?.address || "—"}</span><span className="text-[10px] text-muted-foreground">{e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleDateString("en-AU") : ""}</span></div>
                <div className="truncate text-muted-foreground">{e.subject || "(no subject)"}</div>
              </button>
            ))}
        </div>
      )}
      <Field label="Message (the table is appended below it)"><Textarea rows={5} value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="gc-wiz-email-intro" /></Field>
      <GcCombinedTable rows={combined} total={schedule.total} kind={schedule.kind} maxHeight="max-h-56" />
      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <Button variant="ghost" onClick={() => onFinished(false)} data-testid="gc-wiz-skip-email-btn">Don't send — finish</Button>
        <Button onClick={send} disabled={busy || (emailMode === "new" ? !to.trim() : !replyMsg)} className="bg-slate-900 hover:bg-slate-800" data-testid="gc-wiz-send-email-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}Send to customer
        </Button>
      </div>
    </div>
  );
}
